import { FiBook } from 'react-icons/fi';

export const topicVocabEducationData = {
  courseId: 'topic-vocab-education',
  courseType: 'vocabulary',
  title: 'Topic Vocabulary: Education',
  icon: FiBook,
  overview: {
    impact: 'Master the most frequently tested IELTS topic with specialised vocabulary',
    focus: 'Core education terms, higher education vocabulary, and essay-ready phrases',
    uses: 'Writing Task 2, Speaking Parts 2–3, Reading comprehension',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'Core Education Vocabulary',
      content: `<p>Education is the <strong>most common topic</strong> in IELTS Writing Task 2 and a frequent subject in Speaking Parts 2 and 3. Mastering this vocabulary gives you a ready-made toolkit for a wide range of questions.</p>
<table>
  <thead><tr><th>Word</th><th>Definition</th><th>Example Sentence</th></tr></thead>
  <tbody>
    <tr><td><strong>curriculum</strong></td><td>the subjects and content taught in a school or programme</td><td>The national curriculum should include financial literacy.</td></tr>
    <tr><td><strong>tuition</strong></td><td>teaching or instruction; also fees paid for education</td><td>University tuition fees have risen sharply over the past decade.</td></tr>
    <tr><td><strong>scholarship</strong></td><td>a financial award for study based on merit or need</td><td>She received a scholarship to study engineering abroad.</td></tr>
    <tr><td><strong>graduate</strong> (n/v)</td><td>a person who has completed a degree; to complete a degree</td><td>Many graduates struggle to find employment in their field.</td></tr>
    <tr><td><strong>undergraduate</strong></td><td>a student studying for their first degree</td><td>Undergraduate students must complete 120 credits to graduate.</td></tr>
    <tr><td><strong>distance learning</strong></td><td>studying remotely, often online</td><td>Distance learning has become more popular since the pandemic.</td></tr>
    <tr><td><strong>vocational training</strong></td><td>practical education for a specific job or trade</td><td>Vocational training provides a direct pathway to employment.</td></tr>
    <tr><td><strong>literacy</strong></td><td>the ability to read and write</td><td>Improving adult literacy is essential for economic development.</td></tr>
    <tr><td><strong>compulsory</strong></td><td>required by law or rules</td><td>In most countries, education is compulsory until age 16.</td></tr>
    <tr><td><strong>elective</strong></td><td>a subject chosen by the student, not required</td><td>Students can choose from a range of electives including art and music.</td></tr>
    <tr><td><strong>semester</strong></td><td>a half-year term in an academic calendar</td><td>The course runs for two semesters, from September to June.</td></tr>
    <tr><td><strong>dissertation</strong></td><td>a long research essay written for a degree</td><td>She wrote her dissertation on the impact of bilingual education.</td></tr>
  </tbody>
</table>`,
      points: [
        '"Curriculum" and "tuition" are the two most frequently tested education words in IELTS',
        'Know the difference: "graduate" (completed degree) vs "undergraduate" (studying for first degree)',
        '"Compulsory" and "elective" are opposites — learn them as a pair',
        '"Literacy" can be extended: "digital literacy", "financial literacy", "media literacy"',
      ],
    },
    {
      title: 'Education Systems',
      content: `<p>IELTS essays often ask you to compare education systems or discuss education policy. These terms help you discuss how education is structured and delivered.</p>
<h4>Key Terms</h4>
<ul>
  <li><strong>State/public education:</strong> government-funded schooling available to all</li>
  <li><strong>Private education:</strong> fee-paying schools, often with selective admission</li>
  <li><strong>Inclusive education:</strong> systems designed to accommodate all learners, including those with disabilities</li>
  <li><strong>Standardised testing:</strong> uniform exams administered to all students for comparison</li>
  <li><strong>Rote learning:</strong> memorisation through repetition, without focus on understanding</li>
  <li><strong>Critical thinking:</strong> the ability to analyse and evaluate information independently</li>
  <li><strong>Pedagogy:</strong> the method and practice of teaching</li>
  <li><strong>Dropout rate:</strong> the percentage of students who leave education before completing their course</li>
</ul>`,
      points: [
        '"State education" vs "private education" is a classic IELTS Task 2 comparison topic',
        '"Rote learning" vs "critical thinking" is another common essay debate',
        '"Pedagogy" is a Band 8+ word — use it when discussing teaching methods',
        '"Dropout rate" is useful for problem-solution essays about education access',
      ],
    },
    {
      title: 'Higher Education Vocabulary',
      content: `<p>Many IELTS questions specifically target higher education — university access, fees, benefits of degrees, and alternatives like vocational training.</p>
<h4>Essential Terms</h4>
<ul>
  <li><strong>Tertiary education:</strong> education after secondary school (university, college, vocational)</li>
  <li><strong>Academic qualification:</strong> a degree, diploma, or certificate from a formal institution</li>
  <li><strong>Postgraduate:</strong> study after a first degree (Master's, PhD)</li>
  <li><strong>Tuition fees:</strong> the cost of attending a course or programme</li>
  <li><strong>Student debt/loan:</strong> money borrowed to pay for education</li>
  <li><strong>Campus:</strong> the grounds and buildings of a university</li>
  <li><strong>Faculty:</strong> a department within a university; also the academic staff</li>
  <li><strong>Research-intensive university:</strong> a university focused on producing academic research</li>
</ul>
<h4>Useful Phrases for Essays</h4>
<p><em>"access to higher education", "the rising cost of tuition", "the value of a university degree", "graduate employability", "the expansion of tertiary education"</em></p>`,
      points: [
        '"Tertiary education" is the formal term for university-level study — use it in Task 2',
        'Know the hierarchy: undergraduate → postgraduate → doctoral (PhD)',
        '"Graduate employability" is a key phrase for essays about the purpose of university',
        '"Student debt" and "tuition fees" frequently appear in IELTS reading passages too',
      ],
    },
    {
      title: 'Education in IELTS Writing Topics',
      content: `<p>Education appears in many common Task 2 question types. Here are typical topics and the vocabulary they require.</p>
<h4>Common Essay Questions</h4>
<ul>
  <li><strong>"Should university education be free?"</strong> — tuition fees, student debt, state funding, scholarship, equal access, meritocracy</li>
  <li><strong>"Is rote learning outdated?"</strong> — pedagogy, critical thinking, creativity, standardised testing, curriculum reform</li>
  <li><strong>"Should vocational training be valued equally?"</strong> — vocational vs academic, practical skills, employment pathways, tertiary education</li>
  <li><strong>"What is the role of technology in education?"</strong> — distance learning, digital literacy, blended learning, online resources</li>
</ul>
<h4>Model Sentence</h4>
<p><em>"While tuition fees continue to rise, access to higher education remains a fundamental right. Governments should expand scholarship programmes and invest in vocational training as a viable alternative to traditional academic pathways."</em></p>`,
      points: [
        'Before your exam, prepare vocabulary clusters for 3-4 common education subtopics',
        'The "free university" essay appears frequently — have your key terms ready',
        'Link education vocabulary to other topics: "education + technology", "education + economy"',
        'Practise writing full paragraphs that combine 4-5 education terms naturally',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Word to Definition',
      type: 'select',
      instructions: 'Select the correct definition for each education vocabulary word.',
      questions: [
        {
          text: 'What does "curriculum" mean?',
          options: [
            'a financial award for study',
            'the subjects and content taught in a school',
            'a type of exam',
            'the buildings of a university',
          ],
          answer: 'the subjects and content taught in a school',
          explanation: '"Curriculum" refers to the programme of study — the subjects, topics, and content that a school or educational institution teaches.',
        },
        {
          text: 'What does "vocational training" mean?',
          options: [
            'training to become a university professor',
            'general academic education',
            'practical education for a specific job or trade',
            'learning through memorisation',
          ],
          answer: 'practical education for a specific job or trade',
          explanation: '"Vocational training" is hands-on, practical education designed to prepare students for a specific occupation or trade.',
        },
        {
          text: 'What does "compulsory" mean?',
          options: ['optional', 'expensive', 'required by law or rules', 'popular'],
          answer: 'required by law or rules',
          explanation: '"Compulsory" means mandatory — required by law. Compulsory education means children must attend school.',
        },
        {
          text: 'What does "dissertation" mean?',
          options: [
            'a short classroom test',
            'a long research essay written for a degree',
            'a type of scholarship',
            'a university lecture',
          ],
          answer: 'a long research essay written for a degree',
          explanation: 'A "dissertation" is an extended piece of academic research, typically required for completing a Master\'s or doctoral degree.',
        },
      ],
    },
    {
      title: 'Complete the Sentences',
      type: 'fill-in',
      instructions: 'Fill in the blank with the most appropriate education vocabulary word from the lesson.',
      questions: [
        {
          text: 'In many countries, primary education is _______ for all children between the ages of 6 and 12.',
          answer: 'compulsory',
          explanation: '"Compulsory" means required by law — primary education is mandatory in most countries.',
        },
        {
          text: 'The university offers a range of _______ including photography, music theory, and creative writing.',
          answer: 'electives',
          explanation: '"Electives" are optional subjects that students choose based on their interests, as opposed to required core subjects.',
        },
        {
          text: 'Rising _______ fees have made it increasingly difficult for students from low-income families to attend university.',
          answer: 'tuition',
          explanation: '"Tuition fees" are the charges students pay for their education — a core term in education policy debates.',
        },
        {
          text: 'The government invested heavily in _______ programmes to reduce the adult illiteracy rate.',
          answer: 'literacy',
          explanation: '"Literacy" programmes teach reading and writing skills — "adult literacy" is a common IELTS topic.',
        },
      ],
    },
    {
      title: 'Choose the Correct Word',
      type: 'select',
      instructions: 'Select the word that best completes each sentence.',
      questions: [
        {
          text: '"After completing her _______ degree, she decided to pursue a Master\'s in public policy."',
          options: ['postgraduate', 'undergraduate', 'doctoral', 'vocational'],
          answer: 'undergraduate',
          explanation: 'An "undergraduate" degree is the first degree (Bachelor\'s). After completing it, she moves to "postgraduate" study (Master\'s).',
        },
        {
          text: '"The school has updated its _______ to include coding and digital skills alongside traditional subjects."',
          options: ['scholarship', 'semester', 'curriculum', 'dissertation'],
          answer: 'curriculum',
          explanation: '"Curriculum" is the correct term for the programme of subjects and content taught at a school.',
        },
        {
          text: '"_______ allows students in remote areas to access quality education without travelling to a campus."',
          options: ['Rote learning', 'Distance learning', 'Vocational training', 'Standardised testing'],
          answer: 'Distance learning',
          explanation: '"Distance learning" refers to studying remotely, typically online, without attending a physical campus.',
        },
        {
          text: '"Many employers now value practical skills gained through _______ as much as traditional academic degrees."',
          options: ['electives', 'dissertations', 'vocational training', 'scholarships'],
          answer: 'vocational training',
          explanation: '"Vocational training" provides practical, job-specific skills — increasingly valued alongside academic qualifications.',
        },
      ],
    },
    {
      title: 'Write About Education Topics',
      type: 'writing',
      instructions: 'Write a complete IELTS Task 2-style sentence or short paragraph using the given education vocabulary words.',
      questions: [
        {
          text: 'Write a sentence using "curriculum", "critical thinking", and "rote learning".',
          sampleAnswer: 'The curriculum should be redesigned to prioritise critical thinking over rote learning, as the latter fails to prepare students for the demands of the modern workplace.',
          explanation: 'All three terms are used correctly: "curriculum" (programme of study), "critical thinking" (analytical ability), "rote learning" (memorisation without understanding).',
        },
        {
          text: 'Write a short paragraph (2-3 sentences) using "tuition fees", "scholarship", and "graduate".',
          sampleAnswer: 'The rising cost of tuition fees has made higher education inaccessible for many students from low-income backgrounds. While scholarship programmes can alleviate this burden, they are often limited in number. As a result, many graduates enter the workforce burdened by significant student debt.',
          explanation: 'All three terms are used naturally in a coherent argument about education affordability — a classic IELTS essay topic.',
        },
        {
          text: 'Write a Task 2 sentence about the value of "vocational training" compared to "tertiary education".',
          sampleAnswer: 'Vocational training offers a practical and cost-effective pathway to employment, and it should be valued equally alongside traditional tertiary education rather than being seen as a lesser alternative.',
          explanation: '"Vocational training" and "tertiary education" are contrasted naturally in a sentence making an argument — perfect for an IELTS discussion essay.',
        },
      ],
    },
  ],
  tips: [
    'Education is the #1 IELTS Writing topic — invest extra time mastering this vocabulary',
    'Learn words in topic clusters: "university" → tuition, scholarship, graduate, campus, faculty, dissertation',
    'Know both meanings of "tuition": teaching (British) and fees (American) — IELTS accepts both',
    'Pair education vocabulary with opinion phrases: "It is widely believed that the curriculum should…"',
    'Prepare vocabulary for 4 common subtopics: free education, technology in schools, vocational vs academic, rote learning',
  ],
  dailyPractice: [
    'Write a Task 2 body paragraph on an education topic using at least 5 words from this lesson',
    'Practise a Speaking Part 3 answer about education using education-specific vocabulary',
    'Read a news article about education and note any vocabulary from the lesson that appears',
    'Create a mind map connecting education vocabulary to related IELTS topics (economy, technology, society)',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Topic Vocabulary: Education for IELTS. Core terms (curriculum, tuition, scholarship, graduate, compulsory, elective, literacy, vocational training, distance learning, dissertation), education systems, higher education vocabulary, and common essay topic preparation. Difficulty 3 — intermediate.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...topicVocabEducationData,
      title: topicVocabEducationData.title + ' (Foundations)',
      overview: { ...topicVocabEducationData.overview, duration: '15-20 minutes' },
      sections: topicVocabEducationData.sections.slice(0, Math.max(2, Math.ceil(topicVocabEducationData.sections.length / 2))),
      exercises: topicVocabEducationData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...topicVocabEducationData,
      title: topicVocabEducationData.title + ' (Advanced)',
      overview: { ...topicVocabEducationData.overview, duration: '30-35 minutes' },
      sections: topicVocabEducationData.sections,
      exercises: topicVocabEducationData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(topicVocabEducationData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return topicVocabEducationData;
}
